'use client';

import React from 'react';
import { Clock, LogIn } from 'lucide-react';
import { useLanguage } from '@/components/providers/LanguageProvider';

interface SessionExpiredModalProps {
  isOpen: boolean;
  onSignInAgain: () => void;
}

export function SessionExpiredModal({
  isOpen,
  onSignInAgain,
}: SessionExpiredModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
    >
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl p-6 overflow-hidden animate-in zoom-in-95 duration-200 text-center">
        {/* Top Accent Icon */}
        <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center mb-4 ring-8 ring-amber-500/5">
          <Clock size={28} className="animate-pulse" />
        </div>

        {/* Title & Description */}
        <h2
          id="session-expired-title"
          className="text-lg font-700 text-foreground mb-2"
        >
          {t('session.expired_title')}
        </h2>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          {t('session.expired_desc')}
        </p>

        {/* Action Button */}
        <button
          type="button"
          onClick={onSignInAgain}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground font-600 text-sm hover:opacity-90 active:scale-[0.98] transition-all shadow-md cursor-pointer"
        >
          <LogIn size={16} />
          <span>{t('session.sign_in_again')}</span>
        </button>
      </div>
    </div>
  );
}
